/* eslint-disable no-nested-ternary */
import React, {useState} from 'react';
import {IconButton, makeStyles, SvgIcon} from '@material-ui/core';
import _ from 'lodash';
import {PriceTierWithHasProductsFlag} from '@treez/commons/sharedTypings/priceTier';
import {CloseIcon} from '@treez-ui/core/icons';
import {Page, Section, Title} from '@treez-ui/core/layout';
import {TitleSize} from '@treez-ui/core/layout/Title';
import {SelectInput} from '@treez-ui/core/forms';
import ProductTable, {ProductTableColumn} from '@treez-ui/core/table/ProductTable';
import {ProductField, ProductInCatalog} from '@treez/commons/sharedTypings/product';
import {ProductFieldAttributesToDisplayNameMap} from '@treez/commons/utils/product';
import {Button, ButtonClass} from '@treez-ui/core/buttons';
import {getAmountUomString} from '@treez-ui/core/lib/product';
import {Color} from '@treez-ui/core/styles/Color';
import ProductIconMap from '@treez-ui/core/icons/productTypes/ProductTypeIconMap';
import {ModalType} from '@treez-ui/core/feedback/modals';

import {PriceTierListTable} from './PriceTierListTable';
import {PriceTierWithCatalogPresence} from '~/lib/sharedInterfaces';
import {ReassignPriceTierPayload} from '~/utils/ProductAPI';
import {
    openFeedbackModal,
    closeFeedbackModal,
} from '~/redux/boundActionCreators/UserFeedbackActions';

const {TYPE, BRAND_NAME, AMOUNT, SUBTYPE, SKU, NAME} = ProductField;

type ProductId = number;
type PriceTierId = number;
type ProductIdToPriceTierId = Record<ProductId, PriceTierId>;
interface PriceTierReassignProps {
    isLoading: boolean;
    isLoadError: boolean;
    onClose: () => void;
    onSubmit: (payload: ReassignPriceTierPayload) => void;
    priceTier: PriceTierWithCatalogPresence;
    priceTierOptions: PriceTierWithHasProductsFlag[];
    products: ProductInCatalog[];
    totalCount: number;
}

const PriceTierReassign = (props: PriceTierReassignProps) => {
    const {
        isLoading,
        isLoadError,
        onClose,
        onSubmit,
        priceTier,
        priceTierOptions,
        products,
        totalCount,
    } = props;

    const [selectedPriceTier, setSelectedPriceTier] = useState<PriceTierWithHasProductsFlag>();
    const [productIdToNewPriceTierId, setProductIdToNewPriceTierId] =
        useState<ProductIdToPriceTierId>({});

    const priceTierMap: Record<number, PriceTierWithHasProductsFlag> = _.keyBy(
        priceTierOptions,
        'id'
    );

    const useStyles = makeStyles({
        assignedPriceTierStyle: {
            color: Color.BLUE_INFO,
        },
        priceTierDropdownContainerStyle: {
            width: 320,
            marginTop: 8,
        },
        assignAllButtonStyle: {
            height: 40,
            marginLeft: 24,
            marginTop: 8,
            width: 120,
        },
        productCountStyle: {
            marginTop: 24,
        },
        saveButtonStyle: {
            width: 120,
        },
    });

    const {
        assignAllButtonStyle,
        assignedPriceTierStyle,
        priceTierDropdownContainerStyle,
        productCountStyle,
        saveButtonStyle,
    } = useStyles();

    const getDescriptionCopy = () => {
        return `Reassign products currently assigned to price tier (${priceTier.label}) to a new tier price.`;
    };

    const getProductTableColumns = (): ProductTableColumn[] => [
        {
            field: NAME,
            header: ProductFieldAttributesToDisplayNameMap[NAME],
            render: (p: ProductInCatalog) => p.name,
        },
        {
            field: BRAND_NAME,
            header: ProductFieldAttributesToDisplayNameMap[BRAND_NAME],
            render: (p: ProductInCatalog) => p.brandName,
        },
        {
            field: AMOUNT,
            header: ProductFieldAttributesToDisplayNameMap[AMOUNT],
            render: (p: ProductInCatalog) => {
                return getAmountUomString(Number(p.amount), p.uom);
            },
        },
        {
            field: TYPE,
            header: ProductFieldAttributesToDisplayNameMap[TYPE],
            render: (p: ProductInCatalog) => {
                const TypeIcon = p.type ? ProductIconMap[p.type] : () => <div />;

                return (
                    <div className="aligned">
                        {p.type && (
                            <SvgIcon>
                                <TypeIcon />
                            </SvgIcon>
                        )}
                        <div> {p.type || ''} </div>
                    </div>
                );
            },
        },
        {
            field: SUBTYPE,
            header: ProductFieldAttributesToDisplayNameMap[SUBTYPE],
            render: (p: ProductInCatalog) => p.subtype,
        },
        {
            field: SKU,
            header: ProductFieldAttributesToDisplayNameMap[SKU],
            render: (p: ProductInCatalog) => p.subtype,
        },
        {
            header: 'Price Tier',
            render: (p: ProductInCatalog) => {
                const priceTierId = productIdToNewPriceTierId[p.id];

                if (priceTierId) {
                    const priceTier = priceTierMap[priceTierId];

                    return <div className={assignedPriceTierStyle}>{priceTier?.label}</div>;
                }

                return '';
            },
        },
        {
            header: '',
            render: (p: ProductInCatalog) => {
                return (
                    <Button
                        buttonClass={ButtonClass.PREFERRED}
                        onClick={() => handleAssign(p.id)}
                        text="Assign"
                    />
                );
            },
        },
    ];

    const hasAnyToSubmit = () => {
        return _.isEmpty(productIdToNewPriceTierId) === false;
    };

    const handleAssignAll = () => {
        if (selectedPriceTier) {
            const result = {};

            products.forEach((p) => {
                result[p.id] = selectedPriceTier.id;
            });

            setProductIdToNewPriceTierId({
                ...productIdToNewPriceTierId,
                ...result,
            });
        } else {
            setProductIdToNewPriceTierId({});
        }
    };

    const handleClose = () => {
        if (hasAnyToSubmit()) {
            openFeedbackModal(
                () => {
                    closeFeedbackModal();
                    onClose();
                },
                'Updates Will Be Lost',
                {
                    type: ModalType.WARNING,
                    bodyText:
                        'Any update not saved will be lost. Are you sure you want to proceed?',
                    cancelOption: true,
                    onCancel: () => closeFeedbackModal(),
                    buttonText: 'Yes',
                }
            );
        } else {
            onClose();
        }
    };

    const handleAssign = (productId: number) => {
        if (selectedPriceTier) {
            setProductIdToNewPriceTierId({
                ...productIdToNewPriceTierId,
                [productId]: selectedPriceTier.id,
            });
        } else {
            setProductIdToNewPriceTierId(_.omit(productIdToNewPriceTierId, [productId]));
        }
    };

    const onPriceTierSelect = (value, index) => {
        if (!_.isUndefined(index)) {
            const priceTier = priceTierOptions[index];
            if (priceTier) {
                setSelectedPriceTier(priceTier);
            }
        }
    };

    const onPriceTierClear = () => {
        setSelectedPriceTier(undefined);
    };

    const onSave = () => {
        if (hasAnyToSubmit()) {
            const bulkReassignPayload = toBulkReassignPayload(productIdToNewPriceTierId);

            onSubmit(bulkReassignPayload);
            setProductIdToNewPriceTierId({});
        }
    };

    const toBulkReassignPayload = (
        productToNewPriceTierId: ProductIdToPriceTierId
    ): ReassignPriceTierPayload => {
        const result = {};

        _.map(_.toPairs(productToNewPriceTierId), ([productId, priceTierId]) => {
            if (result[priceTierId]) {
                result[priceTierId] = [...result[priceTierId], productId];
            } else {
                result[priceTierId] = [productId];
            }
        });

        return result;
    };

    return (
        <Page>
            <div className="space-between">
                <Title size={TitleSize.MEDIUM} text="PRICE TIER REASSIGN" />
                <IconButton onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </div>
            <Title size={TitleSize.SMALL} text="CURRENT PRICE TIER" />
            <PriceTierListTable priceTiers={[priceTier]} readOnly />
            <Section>
                <div className="center-box">{getDescriptionCopy()}</div>
                <Title size={TitleSize.SMALL} text="SELECT A PRICE TIER" />
                <div className="aligned">
                    <div className={priceTierDropdownContainerStyle}>
                        <SelectInput
                            label="Price Tier"
                            onChange={onPriceTierSelect}
                            onClear={onPriceTierClear}
                            options={priceTierOptions.map((p) => p.label)}
                            value={selectedPriceTier?.label}
                        />
                    </div>
                    <Button
                        className={assignAllButtonStyle}
                        buttonClass={ButtonClass.PREFERRED}
                        onClick={() => handleAssignAll()}
                        text="Assign All"
                    />
                </div>
            </Section>
            <Section>
                {isLoadError ? (
                    <div className="center-box">Error loading Products to Reassign.</div>
                ) : _.isEmpty(products) && !isLoading ? (
                    <div className="center-box">No Products to Reassign.</div>
                ) : (
                    <>
                        <Title size={TitleSize.SMALL} text="ASSIGN TO" />
                        <ProductTable
                            columns={getProductTableColumns()}
                            fetching={isLoading}
                            values={products}
                        />
                        <div className={productCountStyle}>
                            {isLoading
                                ? 'Loading Products...'
                                : `Showing ${products.length} of ${totalCount} Products`}
                        </div>
                    </>
                )}
            </Section>
            <Section>
                <div className="flex-end">
                    <Button
                        buttonClass={ButtonClass.BASIC}
                        className={saveButtonStyle}
                        onClick={onSave}
                        text="Save"
                        disabled={hasAnyToSubmit() === false}
                    />
                </div>
            </Section>
        </Page>
    );
};

export default PriceTierReassign;
